// Built-in workflow templates (one per task type). A project gets a copy of
// a template's `definition` (see POST /projects/:id/workflows/from-template);
// after that the copy is the project's own and edits never flow back.
//
// Agent states reference roles by name (matched case-insensitively against
// the workspace's `roles.name`); `requiredRoles` lists those names so the
// UI/API can report which roles a workspace still lacks. The names match
// the default role templates (seed.ts / welcome.tsx).

import {
  DEFAULT_WORKFLOW_REF,
  WORKFLOW_INBOX_STATE,
  type ModelTier,
  type WorkflowDecisionSwitch,
  type WorkflowDefinition,
  type WorkflowState,
  type WorkflowTemplate,
} from '../types/workflow.js'
import { KB_WORKFLOW_TEMPLATES } from './kb-bootstrap.js'
import { OPENSPEC_WORKFLOW_TEMPLATES } from './openspec.js'

function agent(
  name: string,
  agentRole: string,
  next: { onSuccess: string; onFailure: string; maxRounds?: number; freshContext?: boolean },
): WorkflowState {
  return {
    name,
    kind: 'agent',
    agentRole,
    requires: [],
    blocks: [],
    freshContext: next.freshContext ?? false,
    onSuccess: next.onSuccess,
    onFailure: next.onFailure,
    maxRounds: next.maxRounds ?? null,
  }
}

function human(name: string, onApprove: string, onReject: string, instructions?: string): WorkflowState {
  return { name, kind: 'human', requires: [], blocks: [], onApprove, onReject, ...(instructions ? { instructions } : {}) }
}

// Knowledge base (docs/plans/2026-10-02-kennisbank-ontwerp.md §7.4): the
// review step asks for what the task taught to land in the knowledge base;
// rejecting sends it back to the developer step, which can write it with
// `kb_write`. Shown to the reviewer with the review.
const KB_REVIEW_INSTRUCTIONS =
  'Before approving, check that what this task taught (where something lives, a convention, a pitfall) is in the knowledge base: a page under project/ added or updated, with applies_to. If it is missing and worth keeping, update the page yourself or reject with a note asking for it.'

interface DecisionOptionInput {
  value: string
  description: string
  next?: string
  /** Continue in another workflow of the project instead of `next` ('@default' + '@inbox': the default workflow's queue). */
  nextWorkflow?: WorkflowDecisionSwitch
  modelTier?: ModelTier
  /** Extra workflow variables this option sets. */
  vars?: Record<string, string>
}

function decisionState(
  name: string,
  config: { question: string; variable: string; options: DecisionOptionInput[]; defaultOption: string; agentRole: string },
): WorkflowState {
  return {
    name,
    kind: 'decision',
    agentRole: config.agentRole,
    requires: [],
    blocks: [],
    question: config.question,
    variable: config.variable,
    options: config.options.map((o) => ({
      value: o.value,
      description: o.description,
      ...(o.next ? { next: o.next } : {}),
      ...(o.nextWorkflow ? { nextWorkflow: o.nextWorkflow } : {}),
      ...(o.modelTier || o.vars
        ? { set: { ...(o.modelTier ? { modelTier: o.modelTier } : {}), ...(o.vars ? { vars: o.vars } : {}) } }
        : {}),
    })),
    defaultOption: config.defaultOption,
  }
}

function terminal(name: string, outcome: 'success' | 'failure' | 'cancelled'): WorkflowState {
  return { name, kind: 'terminal', requires: [], blocks: [], outcome }
}

function definition(
  key: string,
  name: string,
  description: string,
  states: WorkflowState[],
): WorkflowDefinition {
  return { key, name, description, initialState: states[0].name, states, templateKey: key }
}

const featureTemplate: WorkflowTemplate = {
  key: 'feature',
  name: 'Feature',
  description:
    'Planner writes a spec, a human approves it, developer and tester iterate (max 3 rounds), then a human reviews.',
  requiredRoles: ['Planner', 'Developer', 'Tester'],
  definition: definition('feature', 'Feature', 'Spec → build ⇄ test → review.', [
    agent('Planning', 'Planner', { onSuccess: 'Spec review', onFailure: 'Blocked' }),
    human('Spec review', 'Development', 'Planning'),
    agent('Development', 'Developer', { onSuccess: 'Test', onFailure: 'Blocked', maxRounds: 3 }),
    agent('Test', 'Tester', {
      onSuccess: 'Review',
      onFailure: 'Development',
      maxRounds: 3,
      freshContext: true,
    }),
    human('Review', 'Done', 'Development', KB_REVIEW_INSTRUCTIONS),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

// The Feature template with an estimate step: a cheap decision run picks the
// complexity, which sets the model level for the rest of the task and sends
// architecture work through a design step first.
const featureTriageTemplate: WorkflowTemplate = {
  key: 'feature-triage',
  name: 'Feature (with estimate)',
  description:
    'Like Feature, but after the spec is approved an estimate step rates the complexity: it picks the model level for the rest of the task, and architecture work gets a design step first.',
  requiredRoles: ['Planner', 'Developer', 'Tester'],
  definition: definition('feature-triage', 'Feature (with estimate)', 'Spec → estimate → build ⇄ test → review.', [
    agent('Planning', 'Planner', { onSuccess: 'Spec review', onFailure: 'Blocked' }),
    human('Spec review', 'Estimate', 'Planning'),
    decisionState('Estimate', {
      agentRole: 'Planner',
      question:
        'How complex is this task, judging from the approved spec and what the code and knowledge base show? Pick the smallest level that fits.',
      variable: 'complexity',
      options: [
        {
          value: 'routine',
          description: 'Small, well-understood change that follows existing patterns (copy, config, a simple field or endpoint).',
          next: 'Development',
          modelTier: 'light',
        },
        {
          value: 'development',
          description: 'Regular feature work: several files, some design decisions inside the existing architecture.',
          next: 'Development',
          modelTier: 'standard',
        },
        {
          value: 'architecture',
          description: 'Touches the structure of the system (new components, data model, cross-cutting concerns) and needs a design first.',
          next: 'Design',
          modelTier: 'heavy',
        },
      ],
      defaultOption: 'development',
    }),
    agent('Design', 'Planner', { onSuccess: 'Development', onFailure: 'Blocked' }),
    agent('Development', 'Developer', { onSuccess: 'Test', onFailure: 'Blocked', maxRounds: 3 }),
    agent('Test', 'Tester', {
      onSuccess: 'Review',
      onFailure: 'Development',
      maxRounds: 3,
      freshContext: true,
    }),
    human('Review', 'Done', 'Development', KB_REVIEW_INSTRUCTIONS),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

const BUG_REPRODUCE_INSTRUCTIONS =
  'Reproduce the bug. Write a failing test on the task branch that shows it. Put the exact reproduction steps, the environment and the name of the failing test in the task report (write_task_report). Do NOT fix the bug. If you cannot reproduce it, describe what you tried and which information is missing.'

const BUG_REPRODUCED_QUESTION =
  'Was the bug reproduced, and if so, how severe is it? Read the task report (get_task) and look at the failing test. Answer s1 (outage, data loss or security issue, no workaround), s2 (a main function is broken and the workaround is painful), s3 (works wrong but there is a workaround) or s4 (cosmetic or minor). Answer needs_info when there is too little information to reproduce it, not_a_bug when the behavior is intended, or feature when it asks for new behavior.'

const BUG_FIX_INSTRUCTIONS =
  "Make the failing test from the Reproduce step pass without weakening it. The whole test suite stays green."

const BUG_VERIFY_INSTRUCTIONS =
  "With fresh eyes, check that the test from the Reproduce step now passes, that it has not been weakened, and that the whole test suite is green."

const BUG_NEEDS_INFO_INSTRUCTIONS =
  'Add what is missing to the task description (editing is allowed in this step), then approve to reproduce the bug again. Rejecting closes the task as "not a bug".'

function withInstructions(state: WorkflowState, instructions: string): WorkflowState {
  return { ...state, instructions }
}

const bugTemplate: WorkflowTemplate = {
  key: 'bug',
  name: 'Bug',
  description:
    'Tester reproduces the bug with a failing test and rates its severity, developer fixes it, tester verifies (max 3 rounds), then a human reviews. Not reproducible: ask for info. A feature request moves to the default workflow.',
  requiredRoles: ['Tester', 'Developer'],
  definition: definition('bug', 'Bug', 'Reproduce → severity → fix ⇄ verify → review.', [
    withInstructions(agent('Reproduce', 'Tester', { onSuccess: 'Reproduced?', onFailure: 'Blocked', maxRounds: 3 }), BUG_REPRODUCE_INSTRUCTIONS),
    decisionState('Reproduced?', {
      agentRole: 'Tester',
      question: BUG_REPRODUCED_QUESTION,
      variable: 'reproduced',
      defaultOption: 'needs_info',
      options: [
        { value: 's1', description: 'Reproduced. Outage, data loss or security issue; no workaround.', next: 'Fix', vars: { severity: 'S1' } },
        { value: 's2', description: 'Reproduced. A main function is broken and the workaround is painful.', next: 'Fix', vars: { severity: 'S2' } },
        { value: 's3', description: 'Reproduced. Works wrong, but there is a workaround.', next: 'Fix', vars: { severity: 'S3' } },
        { value: 's4', description: 'Reproduced. Cosmetic or minor.', next: 'Fix', vars: { severity: 'S4' } },
        { value: 'needs_info', description: 'Not reproduced: there is too little information.', next: 'Needs info' },
        { value: 'not_a_bug', description: 'The behavior is intended.', next: 'Not a bug' },
        {
          value: 'feature',
          description: 'This asks for new behavior: send it to the default workflow of the project, back to its queue.',
          nextWorkflow: { workflow: DEFAULT_WORKFLOW_REF, state: WORKFLOW_INBOX_STATE },
        },
      ],
    }),
    withInstructions(agent('Fix', 'Developer', { onSuccess: 'Verify', onFailure: 'Blocked', maxRounds: 3 }), BUG_FIX_INSTRUCTIONS),
    withInstructions(
      agent('Verify', 'Tester', {
        onSuccess: 'Review',
        onFailure: 'Fix',
        maxRounds: 3,
        freshContext: true,
      }),
      BUG_VERIFY_INSTRUCTIONS,
    ),
    human('Needs info', 'Reproduce', 'Not a bug', BUG_NEEDS_INFO_INSTRUCTIONS),
    human('Review', 'Done', 'Fix', KB_REVIEW_INSTRUCTIONS),
    terminal('Done', 'success'),
    terminal('Not a bug', 'cancelled'),
    terminal('Blocked', 'failure'),
  ]),
}

const hotfixTemplate: WorkflowTemplate = {
  key: 'hotfix',
  name: 'Hotfix',
  description:
    'Developer fixes straight away, tester verifies, then a human double-checks before it is done. No spec phase.',
  requiredRoles: ['Developer', 'Tester'],
  definition: definition('hotfix', 'Hotfix', 'Fix → verify → human double-check.', [
    agent('Fix', 'Developer', { onSuccess: 'Verify', onFailure: 'Blocked', maxRounds: 2 }),
    agent('Verify', 'Tester', {
      onSuccess: 'Review',
      onFailure: 'Fix',
      maxRounds: 2,
      freshContext: true,
    }),
    human('Review', 'Done', 'Fix'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

const choreTemplate: WorkflowTemplate = {
  key: 'chore',
  name: 'Chore',
  description: 'Developer does the work, a reviewer agent checks it, then a human signs off.',
  requiredRoles: ['Developer', 'Reviewer'],
  definition: definition('chore', 'Chore', 'Do → agent review → human review.', [
    agent('Development', 'Developer', { onSuccess: 'Code review', onFailure: 'Blocked', maxRounds: 3 }),
    agent('Code review', 'Reviewer', { onSuccess: 'Review', onFailure: 'Development', maxRounds: 3 }),
    human('Review', 'Done', 'Development'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

const spikeTemplate: WorkflowTemplate = {
  key: 'spike',
  name: 'Spike',
  description: 'Researcher investigates and writes up findings, then a human reviews them. No pull request.',
  requiredRoles: ['Researcher'],
  definition: definition('spike', 'Spike', 'Research → review.', [
    agent('Research', 'Researcher', { onSuccess: 'Review', onFailure: 'Blocked', maxRounds: 3 }),
    human('Review', 'Done', 'Research'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

// Large work split into subtasks (docs/plans/2026-10-03-subtaken.md §4.3):
// the planner splits the task with `create_subtasks` (its role needs that
// switch on), a person reviews the plan, the subtasks state runs them (each
// through its own workflow, child_workflow empty = the project default) and
// a person reviews the result.
const SPLIT_INSTRUCTIONS =
  'Explore the repository and split this task into subtasks of at most one pull request each, with create_subtasks. ' +
  'Give shared groundwork (configuration, tooling, test setup) its own subtask first and let the others depend on it with dependsOn; ' +
  'keep subtasks that touch the same files out of each other\'s way, or make one wait for the other. ' +
  'Give every subtask concrete acceptance criteria. Finish with a short overview of the plan; do not do the work itself.'

const PLAN_REVIEW_INSTRUCTIONS =
  'Check the subtasks on the task page: is the split complete, are the subtasks small enough, and do the ones that share groundwork wait for it? ' +
  'Add, edit or remove drafts before approving; rejecting sends the task back to the planner.'

const epicTemplate: WorkflowTemplate = {
  key: 'epic',
  name: 'Large work (subtasks)',
  description:
    'Planner splits the task into subtasks, a human approves the plan, the subtasks run (2 at a time, each its own task and PR), then a human reviews.',
  requiredRoles: ['Planner'],
  definition: definition('epic', 'Large work (subtasks)', 'Split → approve plan → run subtasks → review.', [
    { ...agent('Split', 'Planner', { onSuccess: 'Plan review', onFailure: 'Blocked', maxRounds: 3 }), instructions: SPLIT_INSTRUCTIONS },
    human('Plan review', 'Subtasks', 'Split', PLAN_REVIEW_INSTRUCTIONS),
    {
      name: 'Subtasks',
      kind: 'subtasks',
      requires: [],
      blocks: [],
      childWorkflow: null,
      maxParallel: 2,
      onSuccess: 'Review',
      onFailure: 'Review',
    },
    human('Review', 'Done', 'Subtasks'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

export const WORKFLOW_TEMPLATES: readonly WorkflowTemplate[] = [
  featureTemplate,
  featureTriageTemplate,
  bugTemplate,
  hotfixTemplate,
  choreTemplate,
  spikeTemplate,
  epicTemplate,
  ...KB_WORKFLOW_TEMPLATES,
  ...OPENSPEC_WORKFLOW_TEMPLATES,
]

/** Looks up a built-in template by key. */
export function findWorkflowTemplate(key: string): WorkflowTemplate | null {
  return WORKFLOW_TEMPLATES.find((t) => t.key === key) ?? null
}

export type { WorkflowTemplate } from '../types/workflow.js'
